import type p5 from 'p5';
import type { AppState, IAnimationEngine, PersonData, PoseData } from '../../types';
import { DisplayMode, VisualizationType } from '../../types';
import { StickFigureRenderer } from './renderers/StickFigureRenderer';
import { AmbientRenderer } from './renderers/AmbientRenderer';
import { TrailRenderer } from './renderers/TrailRenderer';
import { UIOverlay } from './renderers/UIOverlay';
import type { IArtisticRenderer } from './renderers/IArtisticRenderer';
import { createArtisticRenderer } from './renderers/RendererFactory';
import type { ISceneRenderer, ScenePerson } from './renderers/ISceneRenderer';
import { createSceneRenderer, isSceneVisualization } from './renderers/SceneRendererFactory';
import { PerformanceMonitor } from '../../utils/PerformanceMonitor';
import { MotionTracker } from '../../utils/MotionTracker';
import { sortPeopleByDepth } from '../person-tracking/OcclusionHandler';
import { logger } from '../../utils/logger';

/**
 * AnimationEngine - Main rendering coordinator using p5.js.
 * 
 * Responsibilities:
 * - Initialize and manage p5.js instance
 * - Coordinate all rendering components (stick figure, trails, ambient, UI)
 * - Handle display mode switching (POSE_ANALYSIS vs ARTISTIC)
 * - Manage camera preview rendering
 * - Monitor and optimize rendering performance
 * 
 * Rendering Pipeline:
 * 1. Clear canvas
 * 2. Render ambient animation (if no pose detected)
 * 3. Render trails (if pose detected)
 * 4. Render stick figure (if pose detected)
 * 5. Render camera preview (POSE_ANALYSIS mode only)
 * 6. Render UI overlay (mode toggle, labels, FPS counter)
 * 
 * Performance:
 * - Target: 30 fps (33ms per frame)
 * - Throttled using requestAnimationFrame
 * - Monitors frame times and automatically adjusts quality if needed
 * 
 * Validates Requirements 3.2.1, 3.2.5, 3.2.7, 4.1.1
 */
export class AnimationEngine implements IAnimationEngine {
  private p5Instance: p5 | null = null;
  private currentState: AppState | null = null;
  private stickFigureRenderer: StickFigureRenderer;
  private ambientRenderer: AmbientRenderer;
  private trailRenderer: TrailRenderer;
  private uiOverlay: UIOverlay;
  /** Per-person artistic renderer instances, keyed by personId. */
  private personRenderers: Map<string, { type: VisualizationType; renderer: IArtisticRenderer }>;
  /** Active canvas-wide scene renderer (and its type), or null when none active. */
  private sceneRenderer: { type: VisualizationType; renderer: ISceneRenderer } | null = null;
  private animationFrameId: number | null = null;
  private targetFrameRate = 30;
  private lastFrameTime = 0;
  private frameInterval: number;
  private performanceMonitor: PerformanceMonitor;
  private personMotionTrackers: Map<string, MotionTracker>; // Per-person trackers
  private videoElement: HTMLVideoElement | null = null;
  private onModeToggle: (() => void) | null = null;
  private onDebugToggle: (() => void) | null = null;
  private onVisualizationSelect: ((personId: string, direction: 1 | -1) => void) | null = null;

  // Camera preview configuration
  private readonly previewMaxWidth = 240;
  private readonly previewMaxHeight = 180;
  private readonly previewPadding = 20;

  constructor() {
    this.stickFigureRenderer = new StickFigureRenderer();
    this.ambientRenderer = new AmbientRenderer();
    this.trailRenderer = new TrailRenderer();
    this.uiOverlay = new UIOverlay();
    this.personRenderers = new Map();
    this.frameInterval = 1000 / this.targetFrameRate;
    this.performanceMonitor = new PerformanceMonitor();
    this.personMotionTrackers = new Map(); // Per-person trackers
    
    // Pass performance monitor to UI overlay
    this.uiOverlay.setPerformanceMonitor(this.performanceMonitor);
  }

  /**
   * Initialize the animation engine with a canvas element.
   * Sets up p5.js instance, event handlers, and starts render loop.
   *
   * p5 is imported dynamically so it lands in its own lazily-loaded chunk,
   * keeping it out of the initial bundle (p5 is the largest dependency and is
   * only needed once rendering starts).
   *
   * @param canvas HTML canvas element to render to (will be replaced by p5 canvas)
   */
  async initialize(canvas: HTMLCanvasElement): Promise<void> {
    // Dynamically import p5 so it is code-split into its own chunk
    const p5 = (await import('p5')).default;

    // Get the canvas container
    const container = canvas.parentElement || document.body;
    
    // Create p5 instance in instance mode
    this.p5Instance = new p5((p: p5) => {
      p.setup = () => {
        // Create canvas and attach to container
        const cnv = p.createCanvas(window.innerWidth, window.innerHeight);
        cnv.parent(container);
        p.frameRate(this.targetFrameRate);
        
        // Remove the old canvas element if it exists
        if (canvas && canvas.parentElement) {
          canvas.remove();
        }
      };

      p.draw = () => {
        this.drawFrame(p);
      };

      p.windowResized = () => {
        p.resizeCanvas(window.innerWidth, window.innerHeight);
      };

      // Handle mouse clicks for UI interactions
      p.mousePressed = () => {
        this.handleMouseClick(p);
      };
    });

    // Start render loop
    this.startRenderLoop();
    
    // Start performance monitoring
    this.performanceMonitor.start();
    
    // Expose performance monitor globally for testing
    if (typeof window !== 'undefined') {
      (window as any).performanceMonitor = this.performanceMonitor;
    }
  }

  /**
   * Start the render loop using requestAnimationFrame.
   * Throttles rendering to target frame rate (30 fps).
   * Tracks performance metrics for monitoring.
   * 
   * @private
   */
  private startRenderLoop(): void {
    const renderLoop = (timestamp: number) => {
      // Throttle to target frame rate
      const elapsed = timestamp - this.lastFrameTime;
      
      if (elapsed >= this.frameInterval) {
        this.lastFrameTime = timestamp - (elapsed % this.frameInterval);
        
        // p5.js will handle the actual drawing in its draw() function
        // This loop just ensures we maintain the target frame rate
      }

      this.animationFrameId = requestAnimationFrame(renderLoop);
    };

    this.animationFrameId = requestAnimationFrame(renderLoop);
  }

  /**
   * Draw a single frame
   */
  private drawFrame(p: p5): void {
    if (!this.currentState) {
      return;
    }

    // Record render frame for performance monitoring
    this.performanceMonitor.recordRenderFrame();
    
    // Check rendering performance and adjust quality if needed
    const renderFps = this.performanceMonitor.getRenderingFps();
    if (renderFps > 0 && renderFps < 15) {
      this.reduceQuality();
    }

    // Clear background
    p.background(20, 20, 30);

    const displayMode = this.currentState.displayMode;
    const people = this.currentState.people;
    const hasPeople = people && people.people.size > 0;

    // Scene-scoped (canvas-wide) visualizations take over the whole frame. They
    // are only meaningful in ARTISTIC mode (POSE_ANALYSIS is the technical view).
    // We route generically by visualization scope rather than special-casing any
    // particular visualization, so future canvas-wide effects plug in the same way.
    const sceneType = displayMode === DisplayMode.ARTISTIC ? this.getActiveSceneType() : null;

    if (sceneType !== null) {
      this.ambientRenderer.setFadeAlpha(0);
      this.renderScene(p, sceneType);

      // Debug overlay still applies over a scene (semi-transparent skeletons).
      if (this.currentState.debugOverlayEnabled && hasPeople) {
        for (const person of people.people.values()) {
          if (person.poseData.detected) {
            this.renderDebugOverlay(p, person.poseData);
          }
        }
      }
    } else if (hasPeople) {
      // Multi-person rendering path
      // Get sorted people from OcclusionHandler (back to front)
      const sortedPeople = sortPeopleByDepth(Array.from(people.people.values()));
      
      // Fade out ambient animation
      this.ambientRenderer.setFadeAlpha(0);
      
      // Render each person with their individual config
      for (const person of sortedPeople) {
        this.renderPerson(p, person, displayMode);
      }
    } else {
      // No people detected - render ambient animation
      this.ambientRenderer.setFadeAlpha(1);
      this.ambientRenderer.render(p);
    }

    // Render camera preview (only in POSE_ANALYSIS mode)
    if (displayMode === DisplayMode.POSE_ANALYSIS) {
      this.renderCameraPreview(p);
    }

    // Render UI overlay (always visible, but content changes based on mode)
    this.uiOverlay.render(p, this.currentState);
    
    // Update cursor based on UI hover state
    this.uiOverlay.updateCursor(p);
  }
  
  /**
   * Get or create a motion tracker for a specific person.
   * 
   * @param personId Person identifier
   * @returns MotionTracker instance for this person
   * @private
   */
  private getOrCreateMotionTracker(personId: string): MotionTracker {
    if (!this.personMotionTrackers.has(personId)) {
      this.personMotionTrackers.set(personId, new MotionTracker());
    }
    return this.personMotionTrackers.get(personId)!;
  }

  /**
   * Determine which scene-scoped visualization, if any, should take over the
   * whole canvas this frame.
   *
   * Because visualizations are assigned per person, a canvas-wide scene becomes
   * active when any detected person is assigned a scene-scoped type. The first
   * such person (in iteration order) wins, so the board is coherent even if
   * people momentarily hold different selections.
   *
   * @returns The active scene VisualizationType, or null for the per-person path
   * @private
   */
  private getActiveSceneType(): VisualizationType | null {
    const people = this.currentState?.people;
    if (!people || people.people.size === 0) {
      return null;
    }
    for (const person of people.people.values()) {
      if (person.poseData.detected && isSceneVisualization(person.visualizationType)) {
        return person.visualizationType;
      }
    }
    return null;
  }

  /**
   * Get or create the active scene renderer for the given type, computing motion
   * data for every detected person, then render the canvas-wide scene once.
   *
   * The scene renderer owns the entire canvas (including its background), so the
   * engine does not clear or composite around it.
   *
   * @param p p5 instance
   * @param type The scene-scoped visualization type to render
   * @private
   */
  private renderScene(p: p5, type: VisualizationType): void {
    if (!this.sceneRenderer || this.sceneRenderer.type !== type) {
      const renderer = createSceneRenderer(type);
      if (!renderer) {
        return;
      }
      this.sceneRenderer = { type, renderer };
    }

    const people = this.currentState?.people;
    const scenePeople: ScenePerson[] = [];
    if (people) {
      for (const person of people.people.values()) {
        if (!person.poseData.detected) {
          continue;
        }
        const motionTracker = this.getOrCreateMotionTracker(person.personId);
        motionTracker.update(person.poseData);
        scenePeople.push({ person, motionData: motionTracker.getMotionData() });
      }
    }

    this.sceneRenderer.renderer.render(p, scenePeople);
  }

  /**
   * Get or create the artistic renderer for a person, keyed by their assigned
   * visualization type. If the person's visualization type has changed since the
   * last frame, a fresh renderer is created so stale animation state is dropped.
   *
   * @param person PersonData with personId and visualizationType
   * @returns The artistic renderer instance for this person
   * @private
   */
  private getOrCreateRenderer(person: PersonData): IArtisticRenderer {
    const existing = this.personRenderers.get(person.personId);
    if (existing && existing.type === person.visualizationType) {
      return existing.renderer;
    }
    const renderer = createArtisticRenderer(person.visualizationType);
    this.personRenderers.set(person.personId, { type: person.visualizationType, renderer });
    return renderer;
  }

  /**
   * Render a single person with their individual visual config.
   * Handles both ARTISTIC and POSE_ANALYSIS display modes.
   * 
   * @param p p5 instance
   * @param person PersonData containing pose and visual config
   * @param displayMode Current display mode
   * @private
   */
  private renderPerson(p: p5, person: PersonData, displayMode: DisplayMode): void {
    const pose = person.poseData;
    
    if (!pose.detected) {
      return;
    }
    
    // Get or create motion tracker for this person
    const motionTracker = this.getOrCreateMotionTracker(person.personId);
    
    // Update motion tracker with new pose data
    const motionStartTime = performance.now();
    motionTracker.update(pose);
    const motionEndTime = performance.now();
    const motionTime = motionEndTime - motionStartTime;
    
    // Warn if motion tracking takes too long (>2ms)
    if (motionTime > 2) {
      logger.warn(`⚠️ Motion tracking took ${motionTime.toFixed(2)}ms (target: <2ms)`);
    }
    
    // Get motion data for artistic renderers
    const motionData = motionTracker.getMotionData();
    
    // Render based on display mode
    if (displayMode === DisplayMode.ARTISTIC) {
      // In ARTISTIC mode, select the renderer for this person's visualization type
      const renderer = this.getOrCreateRenderer(person);
      renderer.render(p, pose, person.visualConfig, motionData);
      
      // Render debug overlay if enabled (AFTER artistic visualization)
      if (this.currentState?.debugOverlayEnabled) {
        this.renderDebugOverlay(p, pose);
      }
    } else {
      // In POSE_ANALYSIS mode, use stick figure with trails
      // Update trail buffers for this person
      this.trailRenderer.updateTrailsForPerson(person.personId, pose, person.visualConfig.trailLength);
      
      // Render trails using person's visual config
      this.trailRenderer.renderForPerson(p, person.personId, person.visualConfig);
      
      // Render stick figure with thicker lines for better visibility
      const visualConfig = { ...person.visualConfig };
      visualConfig.stickFigureThickness = 5;
      this.stickFigureRenderer.render(p, pose, visualConfig);
    }
  }
  
  /**
   * Render debug overlay with semi-transparent stick figure.
   * Only called in ARTISTIC mode when debugOverlayEnabled is true.
   * Validates Requirements 3.2.21, 3.2.22, 3.2.27
   * 
   * @param p p5 instance
   * @param pose Current pose data
   * @private
   */
  private renderDebugOverlay(p: p5, pose: PoseData): void {
    // Create debug-specific visual config
    const debugConfig = {
      primaryColor: { r: 255, g: 255, b: 255, a: 0.5 }, // Semi-transparent white
      secondaryColor: { r: 255, g: 255, b: 255, a: 0.3 },
      trailLength: 0, // No trails in debug mode
      trailOpacity: 0,
      stickFigureThickness: 2.5, // Thinner lines than normal mode
    };
    
    // Render stick figure with debug config
    this.stickFigureRenderer.render(p, pose, debugConfig);
  }
  
  /**
   * Reduce visual quality when FPS drops below 15
   */
  private reduceQuality(): void {
    if (!this.currentState) {
      return;
    }
    
    // Only log warning once
    if (!this.qualityReduced) {
      logger.warn('⚠️ Low FPS detected (<15fps). Reducing visual quality...');
      this.qualityReduced = true;
    }
    
    // Reduce trail length to improve performance
    const currentTrailLength = this.currentState.visualConfig.trailLength;
    if (currentTrailLength > 15) {
      const newTrailLength = Math.max(15, Math.floor(currentTrailLength * 0.7));
      this.currentState.visualConfig.trailLength = newTrailLength;
    }
  }
  
  private qualityReduced = false;

  /**
   * Calculate preview dimensions that preserve aspect ratio
   */
  private calculatePreviewDimensions(videoWidth: number, videoHeight: number): { width: number; height: number } {
    if (videoWidth === 0 || videoHeight === 0) {
      return { width: this.previewMaxWidth, height: this.previewMaxHeight };
    }

    const videoAspectRatio = videoWidth / videoHeight;
    const maxAspectRatio = this.previewMaxWidth / this.previewMaxHeight;

    let width: number;
    let height: number;

    if (videoAspectRatio > maxAspectRatio) {
      // Video is wider than preview area - fit to width
      width = this.previewMaxWidth;
      height = this.previewMaxWidth / videoAspectRatio;
    } else {
      // Video is taller than preview area - fit to height
      height = this.previewMaxHeight;
      width = this.previewMaxHeight * videoAspectRatio;
    }

    return { width, height };
  }

  /**
   * Render the camera preview in the bottom-right corner
   * In multi-person mode, shows all people's landmarks with their assigned colors
   * Validates Requirements 9.1, 9.2, 9.3, 9.4
   */
  private renderCameraPreview(p: p5): void {
    if (!this.videoElement) {
      return;
    }

    // Verify it's actually an HTMLVideoElement
    if (!(this.videoElement instanceof HTMLVideoElement)) {
      console.error('videoElement is not an HTMLVideoElement:', this.videoElement);
      return;
    }

    // Check if video is ready to be drawn
    // Video must have valid dimensions and be playing
    if (this.videoElement.readyState < 2 || 
        this.videoElement.videoWidth === 0 || 
        this.videoElement.videoHeight === 0) {
      return;
    }

    // Calculate dimensions that preserve aspect ratio
    const { width: previewWidth, height: previewHeight } = this.calculatePreviewDimensions(
      this.videoElement.videoWidth,
      this.videoElement.videoHeight
    );

    // Calculate position (bottom-right corner with padding)
    const x = p.width - previewWidth - this.previewPadding;
    const y = p.height - previewHeight - this.previewPadding;

    // Draw border/frame around preview
    p.push();
    p.stroke(255, 255, 255, 200); // White border with slight transparency
    p.strokeWeight(3);
    p.noFill();
    p.rect(x - 2, y - 2, previewWidth + 4, previewHeight + 4);
    p.pop();

    // Draw video preview using p5's drawingContext directly
    // This ensures we're using the native canvas API
    try {
      const ctx = (p as any).drawingContext as CanvasRenderingContext2D;
      
      // Apply horizontal mirroring transformation
      ctx.save();
      ctx.translate(x + previewWidth, y);
      ctx.scale(-1, 1);
      
      // Draw the mirrored video
      ctx.drawImage(this.videoElement, 0, 0, previewWidth, previewHeight);
      
      // Restore transformation
      ctx.restore();
    } catch (error) {
      console.error('Error drawing video preview:', error);
    }

    // Draw pose landmarks overlay for all people
    if (this.currentState?.people && this.currentState.people.people.size > 0) {
      this.renderPreviewLandmarks(p, x, y, previewWidth, previewHeight);
    }
  }

  /**
   * Render pose landmarks overlay on camera preview for all people
   * Uses each person's assigned colors
   * Validates Requirements 9.2, 9.3
   */
  private renderPreviewLandmarks(p: p5, previewX: number, previewY: number, previewWidth: number, previewHeight: number): void {
    if (!this.currentState?.people) {
      return;
    }

    p.push();
    
    // Iterate over all people
    this.currentState.people.people.forEach((person) => {
      const pose = person.poseData;
      
      if (!pose.detected) {
        return;
      }

      const personColor = person.visualConfig.primaryColor;
      
      // Draw connections between landmarks
      const connections = [
        // Face
        ['nose', 'leftEye'],
        ['nose', 'rightEye'],
        ['leftEye', 'leftEar'],
        ['rightEye', 'rightEar'],
        
        // Torso
        ['leftShoulder', 'rightShoulder'],
        ['leftShoulder', 'leftHip'],
        ['rightShoulder', 'rightHip'],
        ['leftHip', 'rightHip'],
        
        // Left arm
        ['leftShoulder', 'leftElbow'],
        ['leftElbow', 'leftWrist'],
        
        // Right arm
        ['rightShoulder', 'rightElbow'],
        ['rightElbow', 'rightWrist'],
        
        // Left leg
        ['leftHip', 'leftKnee'],
        ['leftKnee', 'leftAnkle'],
        
        // Right leg
        ['rightHip', 'rightKnee'],
        ['rightKnee', 'rightAnkle'],
      ];

      // Draw connections with person's color
      p.stroke(personColor.r, personColor.g, personColor.b, 200);
      p.strokeWeight(2);
      
      connections.forEach(([start, end]) => {
        const startLandmark = pose.landmarks[start as keyof typeof pose.landmarks];
        const endLandmark = pose.landmarks[end as keyof typeof pose.landmarks];
        
        if (startLandmark && endLandmark && 
            startLandmark.visibility > 0.5 && endLandmark.visibility > 0.5) {
          // Convert normalized coordinates to preview coordinates
          // Note: x coordinates are mirrored for preview
          const x1 = previewX + (1 - startLandmark.x) * previewWidth;
          const y1 = previewY + startLandmark.y * previewHeight;
          const x2 = previewX + (1 - endLandmark.x) * previewWidth;
          const y2 = previewY + endLandmark.y * previewHeight;
          
          p.line(x1, y1, x2, y2);
        }
      });

      // Draw landmark points with person's color
      p.noStroke();
      p.fill(personColor.r, personColor.g, personColor.b, 255);
      
      Object.values(pose.landmarks).forEach((landmark) => {
        if (landmark.visibility > 0.5) {
          // Convert normalized coordinates to preview coordinates
          // Note: x coordinates are mirrored for preview
          const lx = previewX + (1 - landmark.x) * previewWidth;
          const ly = previewY + landmark.y * previewHeight;
          
          p.circle(lx, ly, 6);
        }
      });
    });
    
    p.pop();
  }

  /**
   * Render with the given state
   */
  render(state: AppState): void {
    this.currentState = state;
  }

  /**
   * Handle window resize
   */
  resize(width: number, height: number): void {
    if (this.p5Instance) {
      this.p5Instance.resizeCanvas(width, height);
    }
  }

  /**
   * Set the video element for camera preview
   */
  setVideoElement(video: HTMLVideoElement): void {
    this.videoElement = video;
  }

  /**
   * Set callback for mode toggle button clicks
   */
  setModeToggleCallback(callback: () => void): void {
    this.onModeToggle = callback;
  }

  /**
   * Set callback for debug toggle button clicks
   * Validates Requirements 3.2.23
   */
  setDebugToggleCallback(callback: () => void): void {
    this.onDebugToggle = callback;
  }

  /**
   * Set callback for per-person visualization selector clicks.
   * Invoked with the person's id and a direction (+1 next, -1 previous).
   */
  setVisualizationSelectCallback(callback: (personId: string, direction: 1 | -1) => void): void {
    this.onVisualizationSelect = callback;
  }

  /**
   * Clean up trail buffer for a person who has exited the frame.
   * Should be called when a person is removed from PersonCollection.
   * 
   * @param personId Person identifier to clean up
   */
  cleanupPersonTrails(personId: string): void {
    this.trailRenderer.clearPersonTrails(personId);
  }

  /**
   * Clean up motion tracker for a person who has exited the frame.
   * Should be called when a person is removed from PersonCollection.
   * 
   * @param personId Person identifier to clean up
   */
  cleanupPersonMotionTracker(personId: string): void {
    this.personMotionTrackers.delete(personId);
  }

  /**
   * Clean up the artistic renderer for a person who has exited the frame.
   *
   * @param personId Person identifier to clean up
   */
  cleanupPersonRenderer(personId: string): void {
    this.personRenderers.delete(personId);
  }

  /**
   * Clean up all resources for a person who has exited the frame.
   * Cleans up both trail buffers and motion trackers.
   * 
   * @param personId Person identifier to clean up
   */
  cleanupPerson(personId: string): void {
    this.cleanupPersonTrails(personId);
    this.cleanupPersonMotionTracker(personId);
    this.cleanupPersonRenderer(personId);
  }

  /**
   * Handle mouse click events
   */
  private handleMouseClick(p: p5): void {
    // Check if UI overlay button was clicked
    const clickResult = this.uiOverlay.handleClick(p);
    
    if (clickResult === 'debug') {
      // Debug toggle button was clicked
      if (this.onDebugToggle) {
        try {
          this.onDebugToggle();
        } catch (error) {
          console.error('Error in debug toggle callback:', error);
        }
      }
    } else if (clickResult === true) {
      // Mode toggle button was clicked
      if (this.onModeToggle) {
        try {
          this.onModeToggle();
        } catch (error) {
          console.error('Error in mode toggle callback:', error);
        }
      }
    } else if (clickResult && typeof clickResult === 'object') {
      // Per-person visualization selector arrow was clicked
      if (this.onVisualizationSelect) {
        try {
          this.onVisualizationSelect(clickResult.personId, clickResult.direction);
        } catch (error) {
          console.error('Error in visualization select callback:', error);
        }
      }
    }
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    // Stop performance monitoring
    if (this.performanceMonitor) {
      this.performanceMonitor.stop();
    }

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.p5Instance) {
      this.p5Instance.remove();
      this.p5Instance = null;
    }
  }
}
